// TopicsPanel.jsx
import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { TopicsPanelProvider, useScopedTopicsInstance, useTopicsStoreProvider } from "./Context";
import Thread from "./components/Thread";
import Spinner from "../Spinner";
import Tabs from "../Tabs";
import Dropdown from "@/react/components/Dropdown";
import { useDeleteAllThreads } from "./hooks";

export default function TopicsPanel() {
	const TABS = [{ id: "unresolved", label: "Unresolved", }, { id: "resolved", label: "Resolved", }, { id: "pinned", label: "Pinned", }];

	const topicsInstance = useScopedTopicsInstance();
	const { API_CLIENT, namespace, isNamespaceAdmin } = topicsInstance;

	// // create thread store from zustand
	const [activeTab, setActiveTab] = useState("unresolved");
	const [searchTerm, setSearchTerm] = useState("");
	const inputRef = useRef(null);

	const threadIds = useTopicsStoreProvider(s => s.threadIds);
	
	const { data = [], isFetching: isLoading, isSuccess, error, refetch } = useQuery({
		queryKey: ['threads', 'panel', namespace, activeTab, searchTerm],
		queryFn: async () => {
			return (await API_CLIENT.get('', { params: { type: activeTab, q: searchTerm } })).data;
		},
	});
	const { mutateAsync: deleteAllThreadsMutation, isPending: deleteAllThreadsPending } = useDeleteAllThreads();

	useEffect(() => {
		isSuccess && data && topicsInstance.setThreadsAndComments(data,);
	}, [data, isSuccess, topicsInstance]);

	const handleSearch = e => {
		e.stopPropagation();
		setSearchTerm(inputRef.current?.value.trim() || "");
	};

	const handleDeleteAll = async (isResolved) => {
		const result = await Swal.fire({
			title: `Are you sure?`,
			html: `Are you sure you want to delete all ${isResolved ? 'resolved' : 'unresolved'} Comments?<br>      
			     This action cannot be undone.`,
			icon: 'delete',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: `Delete`
		})

		result.isConfirmed && deleteAllThreadsMutation(isResolved);
	}

	return (
		<TopicsPanelProvider value={{ searchTerm, activeTab }}>
			<div className={`flex flex-col gap-4 h-full transition-all ${deleteAllThreadsPending ? 'thread-comment-loading' : ''}`}>
				{/* Tabs */}
				<div className="pl-3 pr-2 flex items-center">
					<Tabs tabs={TABS} activeTab={activeTab} setActiveTab={setActiveTab} />
					{
						isNamespaceAdmin && (
							<div className="ml-auto">
								<Dropdown>
									<Dropdown.Trigger>
										<button className="btn btn-icon text-xl font-normal bg-transparent hover:text-primary text-grey-text">more_vert</button>
									</Dropdown.Trigger>
									<Dropdown.List>
										<div className="hidden flex flex-col gap-2 text-placeholder whitespace-nowrap" data-arrow="false">
											<span className="hover:text-primary hover:cursor-pointer" onClick={() => handleDeleteAll(true)}>
												Delete resolved Comments
											</span>
											<span className="hover:text-primary hover:cursor-pointer" onClick={() => handleDeleteAll(false)}>
												Delete unresovled Comments
											</span>
										</div>
									</Dropdown.List>
								</Dropdown>
							</div>
						)
					}
				</div>
				{/* Search */}
				<div className="flex gap-2 px-3">
					<input
						ref={inputRef}
						type="text"
						placeholder="Search Comment/Topic"
						className="input-field w-full"
						onKeyDown={e => {
							if (e.key === "Enter") {
								handleSearch(e);
							}
						}}
					/>
					<button className="btn btn-icon-wide" onClick={handleSearch}>
						<span className="material-symbols-rounded">search</span>
					</button>
				</div>

				{/* Threads List */}
				<div className="grow overflow-y-auto flex flex-col threads transition-all" disabled={isLoading}>
					<ThreadList threadIds={threadIds} isSuccess={isSuccess} error={error} refetch={refetch} isLoading={isLoading} />
				</div>
			</div>
			{isLoading && data.length === 0 && (
				<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
					<Spinner />
				</div>
			)}
		</TopicsPanelProvider>
	);
}


function ThreadList({ threadIds, error, isSuccess, refetch, isLoading }) {
	if (error) {
		return <div className="flex flex-col pb-2 items-center gap-2 text-center text-secondary text-label">
			Error loading threads
			<button className="btn btn-secondary bg-opacity-faded text-secondary h-[35px]" onClick={refetch}>
				Retry
				<span className="font-MaterialIconsNew text-lg align-middle" >
					refresh
				</span>
			</button>
		</div>
	}

	if (isSuccess && !isLoading && threadIds.size === 0) {
		return <div className="flex flex-col items-center justify-center py-10 text-center text-grey-text">
			<div className="flex items-center gap-2 mb-2">
				<span className="text-label font-medium">No Comments found</span>
			</div>
			<p className="text-sm text-muted-foreground">
				Let's start by right clicking on a cell to create a new Comment
			</p>
			<div className="mt-4 w-40 h-[1px] bg-border/30" />
		</div>
	}

	return Array.from(threadIds).map(id => <Thread key={id} id={id} />);
}


